import { useI18n } from "../../lib/i18n";

/**
 * Responsible Gambling page. Copy comes 1:1 from operator-supplied text
 * (EN + DE) through the central i18n T object. Helpline phone numbers and
 * URLs are hard-coded (not translated) because they are jurisdictional
 * facts, not language variants. DO NOT change, reformat, or invent numbers.
 */
const HOTLINE_TEL = "0800 137 27 00";
const CRISIS_TEL_1 = "0800 111 0 111";
const CRISIS_TEL_2 = "0800 111 0 222";
const CHECK_DEIN_SPIEL = "check-dein-spiel.de";
const BUNDESWEIT = "bundesweit-gegen-gluecksspielsucht.de";

const telHref = (num) => `tel:${num.replace(/\s+/g, "")}`;
const httpsHref = (host) => `https://${host}`;

export default function Responsible() {
  const { t } = useI18n();
  const numClass = "font-mono tabular-nums text-[#E8ECF4] hover:text-[#2DD4A7] transition-colors";
  const linkClass = "font-mono text-[#2DD4A7] hover:text-[#F5B841] transition-colors underline underline-offset-2";
  const SIGNS = ["li1", "li2", "li3", "li4", "li5"];
  const TIPS  = ["li1", "li2", "li3", "li4"];
  return (
    <article className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7] mb-3">Legal</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-4" data-testid="legal-responsible-title">{t("responsible.title")}</h1>
      <div className="text-sm text-[#8B97AE] leading-relaxed space-y-8">
        <p data-testid="responsible-lead" className="text-[#E8ECF4]">{t("responsible.lead")}</p>

        <section data-testid="responsible-section-age" className="border-l-2 border-white/10 pl-4">
          <h2 className="font-display text-lg font-bold text-[#E8ECF4] mb-2">{t("responsible.age.h")}</h2>
          <p>{t("responsible.age.p")}</p>
        </section>

        <section data-testid="responsible-section-mindset" className="border-l-2 border-white/10 pl-4">
          <h2 className="font-display text-lg font-bold text-[#E8ECF4] mb-2">{t("responsible.mindset.h")}</h2>
          <p>{t("responsible.mindset.p")}</p>
        </section>

        <section data-testid="responsible-section-signs" className="border-l-2 border-[#F5B841]/40 pl-4">
          <h2 className="font-display text-lg font-bold text-[#E8ECF4] mb-2">{t("responsible.signs.h")}</h2>
          <p className="mb-2">{t("responsible.signs.lead")}</p>
          <ul className="list-disc pl-5 space-y-1">
            {SIGNS.map((k) => (<li key={k}>{t(`responsible.signs.${k}`)}</li>))}
          </ul>
        </section>

        <section data-testid="responsible-section-tips" className="border-l-2 border-[#2DD4A7]/40 pl-4">
          <h2 className="font-display text-lg font-bold text-[#E8ECF4] mb-2">{t("responsible.tips.h")}</h2>
          <ul className="list-disc pl-5 space-y-1">
            {TIPS.map((k) => (<li key={k}>{t(`responsible.tips.${k}`)}</li>))}
            <li>
              {t("responsible.tips.li5.pre")}
              <a href={httpsHref(CHECK_DEIN_SPIEL)} target="_blank" rel="noopener noreferrer" className={linkClass} data-testid="responsible-link-check">{CHECK_DEIN_SPIEL}</a>
              {t("responsible.tips.li5.post")}
            </li>
          </ul>
        </section>

        <section data-testid="responsible-section-help" className="border-l-2 border-[#2DD4A7] pl-4 bg-[#2DD4A7]/[0.03] p-4">
          <h2 className="font-display text-lg font-bold text-[#E8ECF4] mb-2">{t("responsible.help.h")}</h2>
          <p className="mb-3">{t("responsible.help.lead")}</p>
          <ul className="space-y-2">
            <li data-testid="responsible-help-hotline">
              <span className="text-[#E8ECF4] font-semibold">{t("responsible.help.hotline.label")}</span>{" "}
              <a href={telHref(HOTLINE_TEL)} className={numClass}>{HOTLINE_TEL}</a>{" — "}
              <span>{t("responsible.help.hotline.hours")}</span>
            </li>
            <li data-testid="responsible-help-online">
              {t("responsible.help.online.pre")}
              <a href={httpsHref(CHECK_DEIN_SPIEL)} target="_blank" rel="noopener noreferrer" className={linkClass}>{CHECK_DEIN_SPIEL}</a>
            </li>
            <li data-testid="responsible-help-local">
              {t("responsible.help.local.pre")}
              <a href={httpsHref(BUNDESWEIT)} target="_blank" rel="noopener noreferrer" className={linkClass}>{BUNDESWEIT}</a>
            </li>
            <li data-testid="responsible-help-crisis">
              <span>{t("responsible.help.crisis.pre")}</span>{" "}
              <a href={telHref(CRISIS_TEL_1)} className={numClass}>{CRISIS_TEL_1}</a>{" "}
              <span className="text-[#8B97AE]">/</span>{" "}
              <a href={telHref(CRISIS_TEL_2)} className={numClass}>{CRISIS_TEL_2}</a>{" "}
              <span>{t("responsible.help.crisis.free")}</span>
            </li>
          </ul>
          <p className="mt-3 text-xs">{t("responsible.help.outside")}</p>
        </section>

        <section data-testid="responsible-section-stepaway" className="border-l-2 border-white/10 pl-4">
          <h2 className="font-display text-lg font-bold text-[#E8ECF4] mb-2">{t("responsible.stepAway.h")}</h2>
          <p>{t("responsible.stepAway.p")}</p>
        </section>

        <p data-testid="responsible-footer" className="pt-8 border-t border-white/10 font-mono text-xs uppercase tracking-widest text-[#F5B841]">{t("responsible.footer")}</p>
      </div>
    </article>
  );
}
